import type { McpConsentTool } from '@sbc/domain-models/agents';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDecideMcpConsent, useMcpConsentRequest } from '@/lib/api/mcp-authorize';

/**
 * The one place a staff member grants an MCP client access to the hosted tool surface:
 * the reads, the knowledge-base authoring tools that save drafts as this staff member and
 * publish only on an explicit approval call, and the team-skill submit tool that opens a PR
 * for review. The page follows the callback itself rather than the server redirecting the
 * POST, so the code travels on a navigation the browser started.
 */
export function McpAuthorizePage({ requestId }: { requestId: string }) {
  const request = useMcpConsentRequest(requestId);
  const decide = useDecideMcpConsent();
  const [failed, setFailed] = useState<string | null>(null);

  const submit = (decision: 'approve' | 'deny') => {
    setFailed(null);
    decide.mutate(
      { requestId, decision },
      {
        onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
        onError: (error) => setFailed(error.message),
      },
    );
  };

  if (request.isPending) {
    return <p className="text-sm text-[var(--color-muted-foreground)]">Loading the request…</p>;
  }

  if (request.isError || !request.data) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>This request is no longer available</CardTitle>
          <CardDescription>
            An approval request lasts ten minutes and can be answered once. Start the connection
            again from Claude.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const { clientHost, callbackOrigin, issuer, tools } = request.data;
  const readTools = tools.filter((tool) => tool.access === 'read');
  const writeTools = tools.filter((tool) => tool.access === 'write');
  const busy = decide.isPending;

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Connect {clientHost} to SBC knowledge and code?</CardTitle>
        <CardDescription>
          Approving lets this client act as you on {issuer}: read the Seoul Beauty Club knowledge
          base, ecommerce source and team skills, save knowledge-base articles as drafts you own,
          publish a draft only when you tell it to, and submit team skills as pull requests for
          review. It cannot delete, archive, merge or touch the ecommerce source.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <dl className="grid grid-cols-[9rem_1fr] gap-y-2 text-sm">
          <dt className="text-[var(--color-muted-foreground)]">Client</dt>
          <dd className="font-medium">{clientHost}</dd>
          <dt className="text-[var(--color-muted-foreground)]">Sends the code to</dt>
          <dd className="font-medium">{callbackOrigin}</dd>
          <dt className="text-[var(--color-muted-foreground)]">Environment</dt>
          <dd className="font-medium">{issuer}</dd>
        </dl>

        <ToolList heading={`It can read with these ${readTools.length} tools:`} tools={readTools} />
        <ToolList
          heading={`It can write with these ${writeTools.length} tools — nothing goes live until it is approved:`}
          tools={writeTools}
        />

        {failed && <p className="text-sm text-[var(--color-destructive)]">{failed}</p>}

        <div className="flex gap-3">
          <Button disabled={busy} onClick={() => submit('approve')}>
            {busy ? 'Working…' : 'Approve'}
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => submit('deny')}>
            Deny
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToolList({ heading, tools }: { heading: string; tools: McpConsentTool[] }) {
  if (tools.length === 0) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{heading}</p>
      <ul className="space-y-2">
        {tools.map((tool) => (
          <li key={tool.name} className="text-sm">
            <Badge variant="outline" className="font-mono">
              {tool.name}
            </Badge>
            <span className="ml-2 text-[var(--color-muted-foreground)]">{tool.description}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
